import {DateOnlyIsoString} from './common.types';
import {joinStrings, truncateTo} from '../transformation.helpers';
import {isPopulatedString, isString} from './typing.helpers';

const ABBREVIATED_MIDDLE_NAME_LENGTH: number = 1;

export function preferredOrFirstNameFrom(preferredName: string, firstName: string): string {
  return isPopulatedString(preferredName) ? preferredName : firstName;
}

export function middleNameIfNotPreferredName(middleName: string, preferredName: string): string {
  return isPopulatedString(preferredName) ? '' : middleName;
}

export function lastNameWithSuffixFrom(lastName: string, suffix: string): string {
  return joinStrings(lastName, suffix);
}

export function fullNameFrom(firstName: string, middleName: string, lastName: string, suffix: string): string {
  return joinStrings(firstName, middleName, lastNameWithSuffixFrom(lastName, suffix));
}

export function fullPreferredNameFrom(firstName: string, middleName: string, lastName: string, suffix: string, preferredName: string): string {
  return fullNameFrom(preferredOrFirstNameFrom(preferredName, firstName), middleNameIfNotPreferredName(middleName, preferredName), lastName, suffix);
}

export function abbreviatedNameFrom(firstName: string, lastName: string, suffix: string): string {
  return joinStrings(firstInitialOf(firstName), lastNameWithSuffixFrom(lastName, suffix));
}

export function abbreviatedPreferredNameFrom(firstName: string, lastName: string, suffix: string, preferredName: string): string {
  return abbreviatedNameFrom(preferredOrFirstNameFrom(preferredName, firstName), lastName, suffix);
}

export function fullNameWithPronounsFrom(firstName: string, middleName: string, lastName: string, suffix: string, pronouns: string): string {
  return joinStrings(fullNameFrom(firstName, middleName, lastName, suffix), pronounsStringFrom(pronouns));
}

export function fullPreferredNameWithPronounsFrom(firstName: string, middleName: string, lastName: string, suffix: string, preferredName: string, pronouns: string): string {
  return fullNameWithPronounsFrom(preferredOrFirstNameFrom(preferredName, firstName), middleNameIfNotPreferredName(middleName, preferredName), lastName, suffix, pronouns);
}

/** `'Firstname Middle Lastname'` &rarr; `'Last, Firs M'` */
export function truncatedNameFrom(firstName: string, middleName: string, lastName: string, firstNameLength: number, lastNameLength: number): string {
  return joinStrings(
    `${truncateTo(lastNameLength, lastName, '')},`,
    truncateTo(firstNameLength, firstName, ''),
    truncateTo(ABBREVIATED_MIDDLE_NAME_LENGTH, middleName, '')
  );
}

export function yearsSince(date: DateOnlyIsoString) {
  return (date === '') ? 0 : getDiffYears(Date.now(), new Date(date));
}

export function getDiffYears(startDate, endDate) {
  return Math.floor(Math.abs(startDate - endDate) / (1000 * 60 * 60 * 24 * 365));
}

export function notificationPreferencesTextFrom(notifyByEmail: boolean, notifyByText: boolean, notifyByPhone: boolean): string {
  const activePreferences: string[] = [
    ...notifyByEmail ? ['Email'] : [],
    ...notifyByText ? ['Text'] : [],
    ...notifyByPhone ? ['Phone'] : []
  ];

  return activePreferences.length === 0 ? 'None' : activePreferences.join(', ');
}

export function getAdditionalFieldIdentifier(fieldName: string): string {
  if (!isString(fieldName)) return '';
  return 'additional_' + fieldName.toLowerCase().replace(/[^a-zA-Z0-9]/g, '_');
}

function firstInitialOf(name: string): string {
  return isPopulatedString(name) ? `${name.charAt(0)}.` : '';
}

function pronounsStringFrom(pronouns: string): string {
  return isPopulatedString(pronouns) ? `(${pronouns})` : '';
}
